import { getDevAuthHeaders } from '../auth/dev-impersonation';
import { getNativeBearerAuthHeaders } from '../auth/native-api-auth';
import { canUseLocalDatabase, isOnline } from '../connectivity';
import { getApiBaseUrl } from '../env';

import { resolveBlockTitlesLocal } from './store';

export type ResolvedBlockTitle = {
  id: string;
  title: string;
  deleted: boolean;
};

const cache = new Map<string, ResolvedBlockTitle>();

export function invalidateBlockTitleCache(blockId?: string): void {
  if (blockId) {
    cache.delete(blockId);
    return;
  }
  cache.clear();
}

export function primeBlockTitle(entry: ResolvedBlockTitle): void {
  cache.set(entry.id, entry);
}

export async function resolveBlockTitles(
  ids: string[],
): Promise<Map<string, ResolvedBlockTitle>> {
  const unique = [...new Set(ids.filter(Boolean))];
  const missing = unique.filter((id) => !cache.has(id));

  if (missing.length > 0 && canUseLocalDatabase()) {
    const localTitles = await resolveBlockTitlesLocal(missing);
    for (const [id, title] of Object.entries(localTitles)) {
      cache.set(id, { id, title, deleted: false });
    }
  }

  const stillMissing = unique.filter((id) => !cache.has(id));
  if (stillMissing.length > 0 && isOnline()) {
    const base = getApiBaseUrl().replace(/\/$/, '');
    const res = await fetch(`${base}/rpc/blocks/resolve`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...getDevAuthHeaders(),
        ...(await getNativeBearerAuthHeaders()),
      },
      body: JSON.stringify({ ids: stillMissing }),
    });
    if (res.ok) {
      const body = (await res.json()) as { blocks: ResolvedBlockTitle[] };
      for (const block of body.blocks ?? []) {
        cache.set(block.id, block);
      }
    }
  }

  const out = new Map<string, ResolvedBlockTitle>();
  for (const id of unique) {
    const hit = cache.get(id);
    if (hit) {
      out.set(id, hit);
    }
  }
  return out;
}
